import { generatePath } from 'react-router-dom';

import { BoardContract, BoardIssue } from '../../api/ach/board';
import { EDIT_AD_GROUP_URL, EDIT_LINE_ITEM_URL } from '../../constants/routeUrls';
import isIORule from './rules';

// One issue plus the contract / insertion order it hangs off. The ledger, the
// inspector and the keyboard queue all address issues through this shape.
export interface IssueRef {
  contract: BoardContract;
  insertionOrderId: number;
  ioName: string;
  issue: BoardIssue;
}

// flattenIssues walks the contract -> line-item -> issue tree in render order, which
// is also the order j/k navigation and the inspector's selection follow.
export function flattenIssues(contracts: BoardContract[]): IssueRef[] {
  const out: IssueRef[] = [];
  contracts.forEach((contract) =>
    contract.line_items.forEach((li) =>
      li.issues.forEach((issue) =>
        out.push({
          contract,
          insertionOrderId: li.insertion_order_id,
          ioName: li.io_name || `IO ${li.insertion_order_id}`,
          issue,
        })
      )
    )
  );
  return out;
}

// The primary entity an issue is about: the insertion order for IO-grain rules,
// otherwise the campaign (ad group).
export function entityLabel(item: IssueRef): string {
  const { issue, ioName, insertionOrderId } = item;
  if (isIORule(issue.rule_id)) {
    return ioName || `Line item ${insertionOrderId}`;
  }
  return issue.campaign_name || `Ad group ${issue.campaign_id}`;
}

// The campaign an io-grain issue is attributed to ("via <campaign>"). '' when the issue
// names no campaign at all. Shared by the row and the IO sub-header's rollup so the two
// can never disagree about what counts as the same campaign.
export function campaignLabel(issue: BoardIssue): string {
  if (issue.campaign_name) {
    return issue.campaign_name;
  }
  return issue.campaign_id ? `campaign ${issue.campaign_id}` : '';
}

export function adGroupHref(item: IssueRef): string {
  return generatePath(EDIT_AD_GROUP_URL, {
    id: String(item.contract.contract_id),
    adGroupId: String(item.issue.campaign_id),
  });
}

export function lineItemPath(contractId: number, lineItemId: number): string {
  return generatePath(EDIT_LINE_ITEM_URL, { id: String(contractId), lineItemId: String(lineItemId) });
}

export function lineItemHref(item: IssueRef): string {
  return lineItemPath(item.contract.contract_id, item.insertionOrderId);
}

export const RESOLUTION_LABEL: Record<string, string> = {
  recovered: 'Recovered',
  no_longer_applicable: 'No longer applicable',
};

// Ack attribution — prefer the display name, fall back to the numeric id, and skip the
// "by …" clause entirely when neither is present.
export function ackAttribution(issue: BoardIssue): string {
  const who = issue.acknowledged_by_name ?? issue.acknowledged_by;
  const parts: string[] = [];
  if (who) {
    parts.push(`by ${who}`);
  }
  if (issue.acknowledged_at) {
    parts.push(new Date(issue.acknowledged_at).toLocaleDateString());
  }
  return parts.join(' · ');
}

// resolutionText renders the read-only resolution line shown on the Resolved tab.
export function resolutionText(issue: BoardIssue): string {
  const reason = RESOLUTION_LABEL[issue.resolution_reason ?? ''] ?? 'Resolved';
  return issue.resolved_at ? `${reason} · ${new Date(issue.resolved_at).toLocaleDateString()}` : reason;
}
